import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../lib';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

export const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    aria-hidden="true"
    className={cn(
      'z-50',
      'overflow-hidden',
      'max-w-[280px]',
      'break-words',
      'bg-background',
      'text-foreground',
      'rounded-md',
      'border',
      'border-border',
      'px-3',
      'py-1.5',
      'text-sm',
      'shadow-md',
      'animate-in',
      'fade-in-0',
      'zoom-in-95',
      'data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0',
      'data-[state=closed]:zoom-out-95',
      'data-[side=bottom]:slide-in-from-top-2',
      'data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2',
      'data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface AccessibleTooltipTriggerProps extends Omit<
  React.ComponentPropsWithoutRef<'span'>,
  'aria-label' | 'children'
> {
  'aria-label': string;
  icon: React.ReactNode;
}

const AccessibleTooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  AccessibleTooltipTriggerProps
>(({ className, icon, ...props }, ref) => {
  return (
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-roledescription="tooltip"
        {...props}
        ref={ref}
        className={className}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        <span aria-hidden="true">{icon}</span>
      </button>
    </TooltipTrigger>
  );
});

interface InfoTooltipProps {
  label: string;
  icon: React.ReactNode;
  contentClassName?: string;
  triggerLabel?: string;
}

function InfoTooltip({
  label,
  icon,
  contentClassName,
  triggerLabel = 'More information',
}: InfoTooltipProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <AccessibleTooltipTrigger aria-label={triggerLabel} icon={icon} />
        <TooltipContent className={contentClassName}>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export {
  Tooltip,
  AccessibleTooltipTrigger,
  InfoTooltip,
  TooltipContent,
  TooltipProvider,
};
